/* import __COLOCATED_TEMPLATE__ from './audience-filter.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type AppService from 'embercom/services/app-service';
import { hasFeature } from 'embercom/helpers/has-feature';
import { tracked } from '@glimmer/tracking';
import { scheduleOnce } from '@ember/runloop';
import { EntityType } from 'embercom/models/data/entity-types';

const EVERYONE_AUDIENCE_SEGMENT_ID = 0;

interface Args {
  doSearch: (aiContentSegmentIds?: string[]) => void;
  selectedValues?: string[];
  activeFilter?: string;
  removeFilter?: (audience?: string) => void;
}

export default class AudienceFilter extends Component<Args> {
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare appService: AppService;

  @tracked mutableSegmentIds: number[] = [];
  readonly entityType = EntityType.Article;

  get canUseEveryoneFilter() {
    return hasFeature('team-kf-everyone-audience-filter', this.appService);
  }

  get hasEveryoneSelected() {
    return this.mutableSegmentIds.includes(EVERYONE_AUDIENCE_SEGMENT_ID);
  }
  lastSyncedPropsString = '';

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (!this.aiContentSegmentsService.isLoading) {
      this.aiContentSegmentsService.loadSegments();
    }
    let initialIds = (this.args.selectedValues || []).map((id) => Number(id));
    this.mutableSegmentIds = initialIds;
    this.lastSyncedPropsString = JSON.stringify([...initialIds].sort());
  }

  @action
  syncWithProps() {
    let propsIds = (this.args.selectedValues || []).map((id) => Number(id));
    let propsString = JSON.stringify([...propsIds].sort());

    if (this.lastSyncedPropsString === propsString) {
      return;
    }

    let currentString = JSON.stringify([...this.mutableSegmentIds].sort());
    if (currentString !== propsString) {
      this.mutableSegmentIds = propsIds;
    }

    this.lastSyncedPropsString = propsString;
  }

  performSearch = () => {
    let stringIds = this.mutableSegmentIds.map((id) => String(id));
    this.args.doSearch(stringIds);
    this.lastSyncedPropsString = JSON.stringify([...this.mutableSegmentIds].sort());
  };

  @action
  handleSegmentChanged() {
    scheduleOnce('afterRender', this, this.performSearch);
  }

  @action
  onEveryoneSelected() {
    if (this.mutableSegmentIds.includes(EVERYONE_AUDIENCE_SEGMENT_ID)) {
      this.mutableSegmentIds = this.mutableSegmentIds.filter(
        (id) => id !== EVERYONE_AUDIENCE_SEGMENT_ID,
      );
    } else {
      this.mutableSegmentIds = [EVERYONE_AUDIENCE_SEGMENT_ID, ...this.mutableSegmentIds];
    }
    scheduleOnce('afterRender', this, this.performSearch);
  }

  @action
  onAllAudiencesSelected() {
    this.mutableSegmentIds = [];
    scheduleOnce('afterRender', this, this.performSearch);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Filters::AudienceFilter': typeof AudienceFilter;
    'knowledge-hub/filterable-list/filters/audience-filter': typeof AudienceFilter;
  }
}
